import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Download, AlertTriangle, PlugZap, Clock, Wallet as WalletIcon, Search } from "lucide-react";
import api from "@/lib/api";
import { usePaged, TablePager } from "@/components/dashboard/Pager";

/* "I think we should just let the admin know what accounts are not reporting.
   If there's no data, we don't need to report."

   So: who stopped, when, and why — and the wallets that were never connected sit
   in their own tab, because nothing broke there and there is nothing to report. */

const KPI = ({ label, value, sub, color, icon: Icon, tid }) => (
  <div className="glass p-4" data-testid={tid}>
    <div className="flex items-center gap-2 mb-1.5">
      {Icon && <Icon size={14} style={{ color: color || "#6B7280" }} />}
      <div className="text-[11px] uppercase tracking-wide text-ink-3">{label}</div>
    </div>
    <div className="font-display text-2xl font-semibold tracking-tight" style={color ? { color } : undefined}>{value}</div>
    {sub && <div className="text-[11px] text-ink-3 mt-0.5">{sub}</div>}
  </div>
);

const WHY_CHIP = {
  session_expired: { text: "Session expired", bg: "rgba(220,38,38,0.12)", fg: "#B91C1C" },
  never_connected: { text: "Never connected", bg: "rgba(107,114,128,0.14)", fg: "#4B5563" },
  idle: { text: "Nothing new", bg: "rgba(245,158,11,0.14)", fg: "#B45309" },
};

export default function NotReporting() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("stopped");
  // Deep-link from the dashboard note: "See the full list" arrives with the wallet
  // already in the search box.
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  // Wallets holding nothing stay hidden until asked for, and the answer sticks.
  const [showEmpty, setShowEmpty] = useState(() => {
    try { return window.localStorage.getItem("nr:show-empty") === "1"; } catch { return false; }
  });
  const revealEmpty = () => {
    setShowEmpty(true);
    setTab("empty");
    try { window.localStorage.setItem("nr:show-empty", "1"); } catch { /* ignore */ }
  };
  const hideEmpty = () => {
    setShowEmpty(false);
    setTab("stopped");
    try { window.localStorage.removeItem("nr:show-empty"); } catch { /* ignore */ }
  };

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/not-reporting");
      setData(r.data);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); }, []);

  const counts = data?.counts || {};
  const rows = useMemo(() => {
    const all = (data?.rows || []).filter((r) => r.group === tab);
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((r) => [r.label, r.member, r.email, r.address].some((v) => (v || "").toLowerCase().includes(needle)));
  }, [data, tab, q]);
  const pager = usePaged(rows, 25);

  const csv = () => {
    const head = ["Wallet", "Member", "Email", "Address", "Last change", "Days silent", "Why", "Last money", "Mining USDT", "Staked CA"];
    const body = rows.map((r) => [r.label, r.member || "", r.email || "", r.address, r.last_change,
      r.silent_days, WHY_CHIP[r.why]?.text || r.why, r.last_money || "", r.mining_usdt, r.staked_ca]);
    const blob = new Blob([[head, ...body].map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n")],
      { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `not-reporting-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6" data-testid="not-reporting-page">
      <div className="flex items-start gap-3 flex-wrap mb-5">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Not reporting</h1>
          <p className="text-sm text-ink-3 mt-1 max-w-3xl">
            A wallet shows <strong>“Synced today”</strong> whenever we look at it — even when CaryPact has had
            nothing to give us for weeks. This is the list of wallets whose numbers have stopped moving, so a
            dead session never again passes for a healthy account.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={load} disabled={busy} className="btn-secondary !py-2" data-testid="not-reporting-refresh">
            <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> {busy ? "Checking…" : "Re-check"}
          </button>
          <button onClick={csv} disabled={!rows.length} className="btn-secondary !py-2" data-testid="not-reporting-csv">
            <Download size={13} /> CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <KPI label="Stopped reporting" value={counts.stopped ?? "—"} sub="was giving us numbers, now silent"
          color="#B91C1C" icon={AlertTriangle} tid="nr-kpi-stopped" />
        <KPI label="Sessions expired" value={data ? (counts.sessions_expired || 0) : "—"}
          sub="across every wallet — one-tap reconnect fixes" color="#DC2626" icon={PlugZap} tid="nr-kpi-expired" />
        <KPI label="Never connected" value={counts.never ?? "—"} sub="team-list tiles — nothing to report"
          icon={WalletIcon} tid="nr-kpi-never" />
        <KPI label="Reporting fine" value={counts.reporting ?? "—"} sub={`of ${counts.total ?? 0} wallets`}
          color="#15803D" icon={Clock} tid="nr-kpi-ok" />
      </div>

      <div className="glass overflow-hidden">
        <div className="p-4 border-b border-black/5 flex items-center gap-2 flex-wrap">
          {[["stopped", `Stopped reporting (${counts.stopped ?? 0})`], ["never", `Never connected (${counts.never ?? 0})`],
            ...(showEmpty ? [["empty", `Hold nothing (${counts.empty ?? 0})`]] : [])].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`nr-tab-${k}`}
              className={`px-3 py-1.5 rounded-xl text-[12px] font-semibold border transition-colors ${
                tab === k ? "border-black/25 bg-white" : "border-black/10 bg-white/60 text-ink-3 hover:border-black/20"}`}>
              {label}
            </button>
          ))}
          <div className="ml-auto relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-3" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email or address"
              data-testid="nr-search"
              className="pl-8 pr-3 py-1.5 rounded-xl text-[12px] border border-black/10 bg-white/70 w-[240px]" />
          </div>
        </div>

        {tab === "empty" && (
          <div className="px-4 py-3 text-[12px] text-ink-3 border-b border-black/5" data-testid="nr-empty-note">
            Empty wallets: CaryPact has nothing to tell us about these because there is nothing in them.
            They are here only because you asked — they are not a problem to fix.
          </div>
        )}
        {tab === "never" && (
          <div className="px-4 py-3 text-[12px] text-ink-3 border-b border-black/5" data-testid="nr-never-note">
            These wallets were only ever seen through someone else’s team list, so CaryPact never gave us their
            earnings and never will until the owner connects their own login once. Nothing has broken here —
            there is simply nothing to report.
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="modern" data-testid="not-reporting-table">
            <thead>
              <tr>
                <th>Wallet</th><th>Member</th><th className="text-right">Last change</th>
                <th className="text-right">Silent</th><th>Why</th><th className="text-right">Mining</th>
                <th className="text-right">Staked CA</th><th></th>
              </tr>
            </thead>
            <tbody>
              {!data ? (
                <tr><td colSpan="8" className="text-center py-10 text-ink-3" data-testid="nr-loading">Checking every wallet…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-10 text-ink-3" data-testid="nr-empty">
                  {tab === "stopped" ? "Nothing has stopped reporting. Every connected wallet is current." : "No wallets here."}
                </td></tr>
              ) : pager.pageItems.map((r) => {
                const chip = WHY_CHIP[r.why] || WHY_CHIP.idle;
                return (
                  <tr key={r.address} data-testid={`nr-row-${r.address}`}>
                    <td>
                      <div className="font-semibold text-[13px]">{r.label}</div>
                      <div className="text-[11px] font-mono text-ink-3">{r.short}</div>
                    </td>
                    <td>
                      <div className="text-[13px]">{r.member || "—"}</div>
                      {r.email && <div className="text-[11px] text-ink-3">{r.email}</div>}
                    </td>
                    <td className="text-right font-mono text-[12px]">
                      {r.exact ? r.since : `before ${r.since}`}
                      <div className="text-[11px] text-ink-3 font-sans">
                        {r.basis === "money" ? "last money in" : "figures frozen since"}
                      </div>
                    </td>
                    <td className="text-right font-mono text-[13px] font-semibold"
                      style={{ color: r.silent_days >= 14 ? "#B91C1C" : r.silent_days >= 7 ? "#B45309" : undefined }}>
                      {r.silent_days}d
                    </td>
                    <td>
                      <span className="px-2 py-0.5 rounded-lg text-[11px] font-semibold"
                        style={{ background: chip.bg, color: chip.fg }} data-testid={`nr-why-${r.address}`}>
                        {chip.text}
                      </span>
                      {r.token_saved_at && <div className="text-[11px] text-ink-3 mt-0.5">signed in {r.token_saved_at}</div>}
                    </td>
                    <td className="text-right font-mono text-[12px]">{r.mining_usdt ? `$${r.mining_usdt.toLocaleString()}` : "—"}</td>
                    <td className="text-right font-mono text-[12px]">{r.staked_ca ? r.staked_ca.toLocaleString() : "—"}</td>
                    <td className="text-right">
                      <button onClick={() => nav(`/wallets?q=${encodeURIComponent(r.address)}`)}
                        className="btn-secondary !py-1 !text-[11px]" data-testid={`nr-open-${r.address}`}>
                        Open in Wallets
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {rows.length > 25 && <TablePager pager={pager} testId="not-reporting-pager" label="wallets" />}

        {(counts.empty ?? 0) > 0 && (
          <div className="px-4 py-3 text-[12px] text-ink-3 flex items-center gap-2 flex-wrap"
            style={{ borderTop: "1px solid rgba(15,18,38,0.06)" }} data-testid="nr-empty-line">
            <span>
              <strong className="font-semibold">{counts.empty}</strong> wallet{counts.empty === 1 ? "" : "s"} hold
              nothing — no mining, no staked CA, no power, no income ever recorded. Nothing to report.
            </span>
            <button onClick={showEmpty ? hideEmpty : revealEmpty} className="underline hover:no-underline"
              data-testid="nr-empty-toggle">{showEmpty ? "Hide them" : "Show them anyway"}</button>
          </div>
        )}
        <div className="px-4 py-3 text-[11px] text-ink-3" style={{ borderTop: "1px solid rgba(15,18,38,0.06)" }}>
          “Silent” counts days since any CaryPact figure for that wallet last changed — mining, earnings, staked CA
          or power. A wallet is listed once it has been silent {data?.days ?? 3} days or more, looking back {data?.window ?? 45} days.
        </div>
      </div>
    </div>
  );
}
